import { DEFAULT_LOCALE, DEFAULT_DATE_FORMAT } from "@/common/date.common";
import { 
  subYears, 
  startOfDay, 
  format, 
  subDays, 
  endOfDay, 
  startOfWeek, 
  endOfWeek, 
  startOfMonth, 
  endOfMonth, 
  subMonths, 
  startOfYear, 
  endOfYear, 
  subWeeks, 
  startOfQuarter, 
  endOfQuarter, 
  subQuarters,
  isSameDay
} from "date-fns";
import { enUS } from 'date-fns/locale';
import { DateRangePreset } from "@/types/report.types";
// Define the type for the data structure expected by the validation function
export interface ReportDateValidationInput {
  dateRangePreset: DateRangePreset;
  startDate?: Date;
  endDate?: Date;
}

// Define the result type for the validation function
export interface DateValidationResult {
  isValid: boolean;
  errorKey?: string;
  path?: string[];
}

/**
 * Formats a date to "H:MM AM/PM • MM/DD/YY" format
 * Example: "7:25 AM • 02/12/25"
 */
export const formatOrderDateTime = (date: Date | string): string => {
  const d = new Date(date);
  
  const time = d.toLocaleTimeString(DEFAULT_LOCALE, { 
    hour: 'numeric',
    minute: '2-digit',
    hour12: true 
  });
  
  const dateStr = d.toLocaleDateString(DEFAULT_LOCALE, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
  
  return `${time} • ${dateStr}`;
};

export const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString(DEFAULT_LOCALE, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

/**
 * Formats a date for payment receipts with full month name, day, year and time
 * Example: "April 27, 2025 at 5:06 PM"
 */
export const formatPaymentDate = (date: Date | string): string => {
  const d = new Date(date);
  
  const dateStr = d.toLocaleDateString(DEFAULT_LOCALE, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
  
  const timeStr = d.toLocaleTimeString(DEFAULT_LOCALE, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
  
  return `${dateStr} at ${timeStr}`;
};

/**
 * Formats seconds into MM:SS format with leading zeros
 * Example: "05:30" for 330 seconds
 */
export const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

/**
 * Formats a date for the DatePicker component.
 * Uses the default format if none is provided.
 */
export const formatDateForPicker = (date: Date, formatString: string = DEFAULT_DATE_FORMAT): string => {
  return format(date, formatString);
};

/**
 * Calculates date range based on preset identifier
 * @param preset The preset identifier (yesterday, lastWeek, etc.)
 * @returns Object containing startDate and endDate, or null if preset is invalid
 */
export const calculateDateRange = (
  preset: DateRangePreset
): { startDate: Date; endDate: Date } | null => {
  const today = new Date();

  switch (preset) {
    case DateRangePreset.TODAY:
      return {
        startDate: startOfDay(today),
        endDate: endOfDay(today),
      };
    case DateRangePreset.YESTERDAY:
      return {
        startDate: startOfDay(subDays(today, 1)),
        endDate: endOfDay(subDays(today, 1)),
      };
    case DateRangePreset.THIS_WEEK_TO_DATE:
      return {
        startDate: startOfWeek(today, { weekStartsOn: 1 }), // Start from Monday of current week
        endDate: endOfDay(today),
      };
    case DateRangePreset.LAST_7_DAYS:
      return {
        startDate: startOfDay(subDays(today, 6)), // 7 days including today
        endDate: endOfDay(today),
      };
    case DateRangePreset.LAST_WEEK: // Previous Mon-Sun
      const lastWeekStart = startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 });
      return {
        startDate: lastWeekStart,
        endDate: endOfWeek(lastWeekStart),
      };
    case DateRangePreset.LAST_MONTH: // Previous full month
      const firstDayLastMonth = startOfMonth(subMonths(today, 1));
      return {
        startDate: firstDayLastMonth,
        endDate: endOfMonth(firstDayLastMonth),
      };
    case DateRangePreset.LAST_QUARTER: // Previous full quarter
      const firstDayLastQuarter = startOfQuarter(subQuarters(today, 1));
      return {
        startDate: firstDayLastQuarter,
        endDate: endOfQuarter(firstDayLastQuarter),
      };
    case DateRangePreset.LAST_6_MONTHS:
      return {
        startDate: startOfDay(subMonths(today, 6)),
        endDate: endOfDay(today),
      };
    case DateRangePreset.THIS_YEAR_TO_DATE:
      return {
        startDate: startOfYear(today),
        endDate: endOfDay(today),
      };
    case DateRangePreset.LAST_YEAR: // Previous calendar year
      const firstDayLastYear = startOfYear(subYears(today, 1));
      return {
        startDate: firstDayLastYear,
        endDate: endOfYear(firstDayLastYear),
      };
    default:
      return null;
  }
}; 

export const formatDateForBackend = (date: Date): string => {
  return format(date, 'yyyy-MM-dd');
};

/**
 * Formats a date range for display in the UI
 * For a single day: "May 17, 2025"
 * For a range: "Jan 1 - Dec 31, 2025"
 */
export const formatDateRangeForDisplay = (startDate: Date, endDate: Date): string => {
  // Use date-fns isSameDay function for clarity
  if (isSameDay(startDate, endDate)) {
    // Format as a single date: "May 17, 2025"
    return format(startDate, 'MMM d, yyyy', { locale: enUS });
  } else {
    // Format as a range: "Jan 1 - Dec 31, 2025"
    const startYear = format(startDate, 'yyyy');
    const endYear = format(endDate, 'yyyy');
    
    // Format start date with or without year depending on if years match
    const startDateStr = format(
      startDate, 
      startYear !== endYear ? 'MMM d, yyyy' : 'MMM d',
      { locale: enUS }
    );
    
    // Format end date always with year
    const endDateStr = format(endDate, 'MMM d, yyyy', { locale: enUS });
    
    return `${startDateStr} - ${endDateStr}`;
  }
};